/* eslint-disable no-underscore-dangle */
import React from 'react';

import { DecoratorNode, LexicalEditor, NodeKey } from 'lexical';

import { ImagesGallery } from '../../plugins';

import {
  $createImagesGalleryNode,
  $isImagesGalleryNode,
} from './ImagesGalleryNode.helpers';

type SerializedImagesGalleryNode = {
  type: 'images-gallery';
  version: 1;
  images: { name: string; id: string }[];
  description?: string;
  currentIndex?: number;
};

export class ImagesGalleryNode extends DecoratorNode<React.ReactNode> {
  images: { name: string; id: string }[];

  editor: LexicalEditor;

  description: string | undefined;

  static getType(): string {
    return 'images-gallery';
  }

  constructor(
    images: { name: string; id: string }[],
    editor: LexicalEditor,
    description?: string,
    key?: NodeKey,
  ) {
    super(key);

    this.images = images;
    this.editor = editor;
    this.description = description;
  }

  getIds(): string[] {
    return this.images.map(({ id }) => id);
  }

  decorate(editor: LexicalEditor): React.ReactElement {
    return (
      <ImagesGallery
        images={this.images}
        description={this.description}
        editable={editor._editable}
      />
    );
  }

  static clone(node: ImagesGalleryNode): ImagesGalleryNode {
    return new ImagesGalleryNode(
      node.images,
      node.editor,
      node.description,
      node.__key,
    );
  }

  createDOM(): HTMLElement {
    return document.createElement('span');
  }

  exportJSON(): SerializedImagesGalleryNode {
    return {
      type: 'images-gallery',
      version: 1,
      images: this.images,
      description: this.description,
    };
  }

  updateDOM(prevNode: ImagesGalleryNode): boolean {
    const isImagesGalleryNode = $isImagesGalleryNode(prevNode);

    if (!isImagesGalleryNode) return true;

    const shouldUpdate =
      this.description !== prevNode.description ||
      this.images.length !== prevNode.images.length ||
      this.images.some((img, index) => {
        const prevImg = prevNode.images[index];
        return !prevImg || img.name !== prevImg.name;
      });

    return shouldUpdate;
  }

  static importJSON(
    serializedNode: SerializedImagesGalleryNode,
  ): ImagesGalleryNode {
    return $createImagesGalleryNode(
      serializedNode.images,
      null as unknown as LexicalEditor,
      serializedNode.description,
    );
  }

  getTextContent(): string {
    return this.description || '';
  }
}
